import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { publicBlogAPI } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  FileText,
  Calendar,
  ArrowRight,
  Loader2,
  Search,
} from 'lucide-react';

const PublicBlogPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const limit = 9;

  useEffect(() => {
    fetchArticles();
  }, [page]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const response = await publicBlogAPI.getArticles(limit, page * limit);
      setArticles(response.data.articles);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Erreur chargement articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              Blog & Actualités
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Découvrez nos conseils d'experts sur les mini-pelles, le BTP et les engins Liugong. 
              Restez informé des dernières actualités du secteur à La Réunion.
            </p>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h2 className="text-2xl font-bold text-slate-700 mb-2">Aucun article pour le moment</h2>
              <p className="text-slate-500">Revenez bientôt pour découvrir nos premiers articles !</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles.map((article) => (
                  <Card key={article.id} className="overflow-hidden hover:shadow-lg transition group">
                    {/* Image */}
                    <div className="h-48 bg-gradient-to-br from-orange-400 to-orange-600 relative overflow-hidden">
                      {article.featured_image ? (
                        <img
                          src={article.featured_image}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition"
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <FileText className="w-16 h-16 text-white/50" />
                        </div>
                      )}
                    </div>
                    
                    <CardContent className="p-5">
                      {/* Keywords */}
                      {article.keywords && article.keywords.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {article.keywords.slice(0, 2).map((kw, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">
                              {kw}
                            </Badge>
                          ))}
                        </div>
                      )}
                      
                      {/* Title */}
                      <h2 className="text-xl font-bold text-slate-800 mb-2 line-clamp-2 group-hover:text-orange-600 transition">
                        {article.title}
                      </h2>
                      
                      {/* Excerpt */}
                      <p className="text-slate-600 text-sm mb-4 line-clamp-3">
                        {article.excerpt || article.meta_description}
                      </p>
                      
                      {/* Footer */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Calendar className="w-4 h-4" />
                          {formatDate(article.published_at)}
                        </div>
                        <Link to={`/portail/blog/${article.slug}`}>
                          <Button variant="ghost" size="sm" className="text-orange-600 hover:text-orange-700">
                            Lire <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <Button
                    variant="outline"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    Précédent
                  </Button>
                  <span className="text-sm text-slate-600 mx-4">
                    Page {page + 1} sur {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    Suivant
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* SEO Section */}
      <section className="bg-white py-12 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
            Votre partenaire mini-pelles à La Réunion
          </h2>
          <p className="text-slate-600 mb-6">
            IDMAT OI est le distributeur officiel Liugong à La Réunion. 
            Nous proposons une gamme complète de mini-pelles et d'engins de chantier adaptés aux professionnels et aux particuliers.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/portail/machines">
              <Button className="bg-orange-500 hover:bg-orange-600">
                Voir nos machines
              </Button>
            </Link>
            <Link to="/portail/contact">
              <Button variant="outline">
                Nous contacter
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PublicBlogPage;
